/**
 * Brand-scoped category tiles: the subcategories of the current scope that
 * actually contain the brand's items, rendered with the theme's native
 * category-cell markup above the item grid.
 *
 * Data, all from public endpoints the site already uses:
 *   - category tree  : /api/navigation/v1/categorynavitems/tree (one fetch per page life)
 *   - which can match: the "commercecategoryname" facet already present in the
 *                      results page's own response (names only, no counts)
 *   - exact counts   : one Item Search call per candidate category
 *                      (commercecategoryurl + manufacturer facet, limit 1 -> total),
 *                      memoised per brand + category, served from the CDN cache
 *
 * The core's Categories.Model is a private module (Safe Mode refuses it), which
 * is why the native category cells are not reused.
 */
define('Clemtex.Brands.Tiles', [
    'Clemtex.Brands.Utils',
    'Clemtex.Brands.Home',
    'clemtex_brands_tiles.tpl',
    'jQuery',
    'underscore'
], function (Utils, Home, tilesTemplate, jQuery, _) {
    'use strict';

    var TREE_FIELDS = 'internalid,name,fullurl,thumbnailurl,sequencenumber';
    var apiTemplate = null;
    var treePromise = null;
    var countCache = {};
    var installed = false;

    // Site parameters: learned from the core's Item Search calls, else from the
    // page environment (a cold load fetches the category model before any
    // item call has been seen).
    function siteParams() {
        if (apiTemplate) { return apiTemplate.params; }
        var env = window.SC && SC.ENVIRONMENT;
        var site = env && env.siteSettings;
        return env && env.companyId && site && site.siteid ? { c: env.companyId, n: site.siteid } : null;
    }

    // The core's Categories.Model endpoint (private module, so we call the
    // service ourselves): one category with breadcrumb, siblings and children.
    var NAV_FIELDS = '&category_fields=internalid,name,description,pagetitle,pageheading,pagebannerurl,addtohead,' +
        'metakeywords,metadescription,displayinsite,urlfragment,idpath,fullurl,isprimaryurl' +
        '&bread_crumb_fields=internalid,name,displayinsite' +
        '&side_menu_fields=name,internalid,sequencenumber,urlfragment,fullurl,displayinsite' +
        '&subcategory_fields=name,description,internalid,sequencenumber,urlfragment,thumbnailurl,displayinsite';

    function categoryNavUrl(fullurl) {
        var p = siteParams();
        if (!p) { return null; }
        return '/api/navigation/v1/categorynavitems?c=' + p.c + '&n=' + p.n + '&site_id=' + p.n +
            '&full_url=' + encodeURIComponent(fullurl) + '&fullurl=' + encodeURIComponent(fullurl) +
            '&exclude_empty=false&use_pcv=T&pcv_all_items=F' + NAV_FIELDS;
    }

    function getTree() {
        var p = siteParams();
        if (!treePromise && p) {
            treePromise = jQuery.ajax({
                url: '/api/navigation/v1/categorynavitems/tree?c=' + p.c + '&n=' + p.n + '&site_id=' + p.n +
                     '&menu_fields=' + TREE_FIELDS + '&pcv_all_items=F',
                dataType: 'json',
                clemtexPassthrough: true
            }).then(function (response) { return (response && response.data) || response || []; });
            treePromise.fail(function () { treePromise = null; });
        }
        return treePromise || jQuery.Deferred().reject().promise();
    }

    function countFor(brand, categoryUrl) {
        var key = brand.slug + '|' + categoryUrl;
        if (!countCache[key]) {
            countCache[key] = jQuery.ajax({
                url: Utils.countUrl(apiTemplate, brand, categoryUrl),
                dataType: 'json',
                clemtexPassthrough: true
            }).then(function (response) { return (response && response.total) || 0; },
                    function () { delete countCache[key]; return jQuery.Deferred().resolve(0).promise(); });
        }
        return countCache[key];
    }

    function countMany(brand) {
        return function (nodes, callback) {
            jQuery.when.apply(jQuery, _.map(nodes, function (node) { return countFor(brand, node.fullurl); }))
                .always(function () { callback(_.toArray(arguments)); });
        };
    }

    function place(view, html) {
        view.$('.clemtex-brands-tiles').remove();
        if (!html) { return; }
        var $anchor = view.$('#banner-section-top');
        if ($anchor.length) { $anchor.after(html); return; }
        $anchor = view.$('[data-view="Facets.FacetsDisplay"]');
        if ($anchor.length) { $anchor.before(html); return; }
        view.$('[data-view="Facets.Items"]').before(html);
    }

    return {
        getTree: getTree,
        categoryNavUrl: categoryNavUrl,

        install: function install() {
            if (installed) { return; }
            installed = true;
            // learn the Item Search API base + site parameters from the core's own calls
            jQuery.ajaxPrefilter(function (options) {
                if (options.clemtexPassthrough || !Utils.isItemsApi(options.url)) { return; }
                var template = Utils.parseApiTemplate(options.url);
                if (template && template.params.c && template.params.n) { apiTemplate = template; }
            });
        },

        // view: the brand-mode Facets.Browse.View; settings: { minItems, skipSingle, excludeUrls }
        render: function render(view, settings) {
            var brand = view.clemtexBrand;
            var token = (view.__clemtexTilesToken = _.uniqueId('tiles'));
            if (!brand || !apiTemplate) { return; }

            // Brand home: intro panel + card navigation instead of top-level tiles.
            if (!view.clemtexCategoryUrl) {
                Home.render(view, settings, { getTree: getTree, countMany: countMany, place: place }, function () {
                    return view.__clemtexTilesToken === token;   // a newer render superseded this one
                });
                return;
            }

            // Tiles describe the brand within the current category, not the current
            // refinement — like native category tiles, they do not change when a
            // filter is ticked. The names shortcut comes from the page's response,
            // which IS refined, so it is only trusted on unrefined pages.
            var refined = _.some(view.translator.getAllFacets(), function (facet) { return facet.id !== Utils.FACET_ID; });

            getTree().then(function (tree) {
                Utils.resolveTiles({
                    tree: tree,
                    scopeUrl: view.clemtexCategoryUrl,
                    names: refined ? {} : Utils.namesFromFacets(view.model && view.model.get('facets')),
                    excludeUrls: settings.excludeUrls,
                    minItems: settings.minItems,
                    skipSingle: settings.skipSingle,
                    countMany: countMany(brand)
                }, function (tiles) {
                    if (view.__clemtexTilesToken !== token) { return; }   // a newer render superseded this one
                    place(view, tiles.length ? tilesTemplate({
                        tiles: _.map(tiles, function (tile) {
                            return {
                                name: tile.node.name,
                                count: tile.count,
                                url: Utils.brandUrl(brand, tile.node.fullurl),
                                image: tile.node.thumbnailurl || '',
                                hasImage: !!tile.node.thumbnailurl
                            };
                        })
                    }) : '');
                });
            });
        }
    };
});
